import { createResourceContentSelector } from 'util/selector/resourceSelector';

import { createSelector } from '@reduxjs/toolkit';
import { UserRoleType } from 'model/UserRoleType';
import { AdminState } from 'reduxStore/appStore';
import { AuthState } from 'reduxStore/auth/initialState';
import { getIsAdminOrCircleAdmin } from 'shared/utils/getIsAdminOrCircleAdmin';

export const selectAuthDomain = (state: AdminState) => state.auth as AuthState.Domain;

export const selectLoggedUserResource = createSelector(
  selectAuthDomain,
  (domain) => domain.loggedUserResource,
);

export const selectLoggedUser = createResourceContentSelector(selectLoggedUserResource);

export const selectCanCreatePastOnDemandProjects = createSelector(selectLoggedUser, (user) =>
  getIsAdminOrCircleAdmin(user.roles),
);

export const selectUserRoles = createSelector(selectLoggedUser, (user) => user?.roles || []);

export const selectUserId = createSelector(selectLoggedUser, (user) => user?.id || '');

export const selectUserExpiration = createSelector(selectLoggedUser, (user) => user?.exp);

export const selectIsClientAdmin = createSelector(selectUserRoles, (roles) =>
  roles?.includes(UserRoleType.ClientAdmin),
);

export const selectIsWBAdmin = createSelector(selectUserRoles, (roles) =>
  roles?.includes(UserRoleType.Admin),
);
export const selectIsCircleAdmin = createSelector(selectUserRoles, (roles) =>
  roles?.includes(UserRoleType.CircleAdmin),
);

export const selectIsGlobalQuestionLibraryEditor = createSelector(
  selectUserRoles,
  (roles) =>
    roles.includes(UserRoleType.Admin) && roles.includes(UserRoleType.QuestionLibraryEditor),
);

export const selectIsAdmin = createSelector(selectUserRoles, getIsAdminOrCircleAdmin);

export const selectUserFirstName = createSelector(selectLoggedUser, (user) => user?.firstName);

export const selectUserLastName = createSelector(selectLoggedUser, (user) => user?.lastName);

export const selectUserEmail = createSelector(selectLoggedUser, (user) => user?.username);

export const selectShortenUserFullName = createSelector(
  selectUserFirstName,
  selectUserLastName,
  (firstName, lastName) => firstName && `${firstName} ${lastName ? lastName[0] + '.' : ''}`,
);

export const selectUserName = createSelector(
  selectShortenUserFullName,
  selectUserEmail,
  (userFullName, userEmail) => (userFullName ? userFullName : userEmail),
);
